import * as logger from "./logger";

/**
 * Logging-only diagnostics for two suspected Drive logout races (MYG-147181, MYG-147182).
 *
 * MYG-147181: logout creates an UnknownDriverId DriverChange; the DriverChange sync started by logout then runs
 * checkIfCurrentDriver and dispatches VehicleRemoved/SyncDriverChange mid-logout.
 * MYG-147182: VehicleRemoved is persisted as an active event during logout and the SyncDriverChange/SyncHos
 * listener replays it while the logout progress flow is still running.
 *
 * This module never changes behaviour: every entry point is a no-op unless a logout is in progress, swallows its
 * own errors, and only writes `logger.Event.LOGOUTRACE` events (uploaded via TraceText as `drive-event`).
 * Each race kind is logged at most once per logout; repeats are only counted and reported in the
 * `logoutStalled` / `logoutEnded` summaries, which are emitted only when a race or a stall was seen.
 */

export type TLogoutFlow = "legacy" | "react";
export type TLogoutPhase = "started" | "hosSync" | "selectiveSync";
export type TLogoutOutcome = "completed" | "cancelled" | "failed" | "superseded";
export type TLogoutRaceKind = "driverChangeVehicleRemoved" | "vehicleRemovedPersisted" | "vehicleRemovedReplayed";
export type TDriverChangeSyncStep = "checkIfCurrentDriver" | "syncDriverChangeDispatched";

type TCounterName = TLogoutRaceKind | TDriverChangeSyncStep;
type TDetails = Record<string, string | number | boolean | undefined | null>;

// How long logout may keep running after its DriverChange sync finished before we report it as stalled.
export const LOGOUT_STALL_WATCHDOG_MS = 60 * 1000;

interface ILogoutDiagnosticsState {
    flow: TLogoutFlow;
    user: unknown;
    startedAt: number;
    phase: TLogoutPhase;
    counts: Partial<Record<TCounterName, number>>;
    loggedKinds: Set<TLogoutRaceKind>;
    driverChangeSyncedAt?: number;
    watchdog?: ReturnType<typeof setTimeout>;
    stalled: boolean;
}

let state: ILogoutDiagnosticsState | null = null;

function safely(fn: () => void): void {
    try {
        fn();
    } catch {
        // Diagnostics must never affect the logout flow.
    }
}

function currentPage(): string {
    return (typeof location !== "undefined" && location.hash ? location.hash.split(",")[0] : "") || "none";
}

function emit(logout: ILogoutDiagnosticsState, kind: string, details?: TDetails): void {
    const now = Date.now();
    const payload = {
        tickets: "MYG-147181,MYG-147182",
        kind,
        flow: logout.flow,
        phase: logout.phase,
        page: currentPage(),
        elapsedMs: now - logout.startedAt,
        sinceDriverChangeSyncMs: logout.driverChangeSyncedAt === undefined ? null : now - logout.driverChangeSyncedAt,
        counts: logout.counts,
        ...details
    };
    logger.event(logger.Event.LOGOUTRACE, JSON.stringify(payload), logout.user);
}

function increment(name: TCounterName): void {
    if (state) {
        state.counts[name] = (state.counts[name] ?? 0) + 1;
    }
}

function clearWatchdog(): void {
    if (state?.watchdog !== undefined) {
        clearTimeout(state.watchdog);
        state.watchdog = undefined;
    }
}

export function isLogoutInProgress(): boolean {
    return state !== null;
}

export function setLogoutDiagnosticsPhase(phase: TLogoutPhase): void {
    safely(() => {
        if (state) {
            state.phase = phase;
        }
    });
}

/**
 * Marks the end of a logout. Emits a `logoutEnded` summary only if a race or stall was seen during this logout,
 * so ordinary logouts produce no events.
 */
export function endLogoutDiagnostics(outcome: TLogoutOutcome): void {
    safely(() => {
        if (!state) {
            return;
        }
        clearWatchdog();
        const ended = state;
        state = null;
        if (ended.loggedKinds.size > 0 || ended.stalled) {
            emit(ended, "logoutEnded", { outcome, stalled: ended.stalled });
        }
    });
}

/**
 * Marks the start of a logout. Call once the logout has committed to syncing (sync tasks disabled).
 */
export function startLogoutDiagnostics(flow: TLogoutFlow, user: unknown): void {
    safely(() => {
        if (state) {
            endLogoutDiagnostics("superseded");
        }
        state = {
            flow,
            user,
            startedAt: Date.now(),
            phase: "started",
            counts: {},
            loggedKinds: new Set(),
            stalled: false
        };
    });
}

/**
 * Records a DriverChange sync step that ran while logout was in progress (MYG-147181). Counted only; the counts ride
 * along on every emitted event. When the sync dispatches SyncDriverChange (i.e. it finished), the stall watchdog is
 * armed: if logout has not ended within LOGOUT_STALL_WATCHDOG_MS, a single `logoutStalled` event is logged.
 */
export function noteDriverChangeSyncDuringLogout(step: TDriverChangeSyncStep): void {
    safely(() => {
        if (!state) {
            return;
        }
        increment(step);
        if (step !== "syncDriverChangeDispatched" || state.watchdog !== undefined || state.stalled) {
            return;
        }
        state.driverChangeSyncedAt = Date.now();
        const armedFor = state;
        state.watchdog = setTimeout(() => safely(() => {
            if (state !== armedFor) {
                return;
            }
            armedFor.watchdog = undefined;
            armedFor.stalled = true;
            emit(armedFor, "logoutStalled", { watchdogMs: LOGOUT_STALL_WATCHDOG_MS });
        }), LOGOUT_STALL_WATCHDOG_MS);
    });
}

/**
 * Logs a race-shaped event seen while logout was in progress. Logged at most once per kind per logout; further
 * occurrences are counted. `getDetails` is only evaluated when the event is actually logged.
 */
export function noteLogoutRace(kind: TLogoutRaceKind, getDetails?: () => TDetails): void {
    safely(() => {
        if (!state) {
            return;
        }
        increment(kind);
        if (state.loggedKinds.has(kind)) {
            return;
        }
        state.loggedKinds.add(kind);
        emit(state, kind, getDetails?.());
    });
}

/** Test-only reset of module state. */
export function resetLogoutRaceDiagnostics(): void {
    clearWatchdog();
    state = null;
}
